import { useEffect, useMemo, useRef, useState } from "react";
import { components } from "react-select";
import _ from "lodash";
import { useFormContext } from "react-hook-form";
import GeneralSelect, { GeneralSelectProps } from "./GeneralSelect";
import { useInfiniteScroll } from "../../hooks";
import { OptionType } from "../../interface/OptionTypes";
import { t } from "i18next";

type ApiOption = OptionType & {
  raw?: any;
  hasChildren?: boolean;
};

export interface GeneralSelectApiProps
  extends Omit<GeneralSelectProps, "options"> {
  endpoint: string;
  allParams?: Record<string, any>;
  onInputChange?: (inputValue: string) => void;
  enabled?: boolean;
  readOnly?: boolean;
  dependsOn?: any;
  keyKey?: string;
  valueKey?: string;
  labelKey?: string;
  itemsPath?: string;
}

const GeneralSelectApi = ({
  endpoint,
  allParams,
  onInputChange,
  placeholder,
  name,
  onChange,
  isPending,
  enabled = true,
  readOnly,
  dependsOn,
  keyKey = "Id",
  valueKey = "Id",
  labelKey = "Name",
  itemsPath = "Data",
  ...rest
}: GeneralSelectApiProps) => {
  const [options, setOptions] = useState<ApiOption[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>("");

  const queryEnabled =
    enabled && (dependsOn !== undefined ? !!dependsOn : true);

  const lastParamsRef = useRef<Record<string, any> | undefined>();
  const lastEndpointRef = useRef<string | undefined>(undefined);

  const {
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLoading,
    fetchedData,
    isSuccess,
  } = useInfiniteScroll({
    endpoint,
    getAllParams: {
      Name: searchQuery,
      ...allParams,
    },
    enabled: queryEnabled,
  });

  // Extract items from infinite pages (or single response) using itemsPath
  const items = useMemo(() => {
    const getItemsFromPage = (page: any) => {
      const arr = _.get(page, itemsPath);
      return Array.isArray(arr) ? arr : [];
    };

    const pages = fetchedData?.pages;
    if (Array.isArray(pages)) return pages.flatMap(getItemsFromPage);

    return getItemsFromPage(fetchedData);
  }, [fetchedData, itemsPath]);

  const methods = useFormContext();
  const selectedValue = methods.watch(name);

  const resolveSelected = () => {
    if (rest.isMulti) {
      if (Array.isArray(selectedValue)) {
        // array of option objects already
        if (selectedValue.length && typeof selectedValue[0] === "object")
          return selectedValue;
        // array of primitive values
        return options.filter((o) => selectedValue.includes(o.value));
      }
      return [];
    }

    // ✅ allow storing the full option object
    if (
      selectedValue &&
      typeof selectedValue === "object" &&
      "value" in selectedValue
    ) {
      return selectedValue;
    }

    return options.find((o) => o.value === selectedValue) || null;
  };

  const selectedOption = resolveSelected();

  // Reset options when endpoint / params change (prevents stale merges)
  useEffect(() => {
    const endpointChanged = lastEndpointRef.current !== endpoint;
    const paramsChanged = !_.isEqual(lastParamsRef.current, {
      Name: searchQuery,
      ...allParams,
    });

    if (endpointChanged || paramsChanged) {
      lastEndpointRef.current = endpoint;
      lastParamsRef.current = _.cloneDeep({
        Name: searchQuery,
        ...allParams,
      });
      setOptions([]);
    }
  }, [endpoint, allParams, searchQuery]);

  useEffect(() => {
    if (!queryEnabled || !isSuccess) return;

    const dataOptions: ApiOption[] = items.map((item: any) => ({
      key: String(_.get(item, keyKey) ?? ""),
      value: _.get(item, valueKey),
      label: Array.isArray(labelKey)
        ? labelKey.map((label) => String(_.get(item, label))).join(" ")
        : String(_.get(item, labelKey) ?? ""),
      ...item,
      hasChildren: item?.HasChildren,
    }));

    const defaultVal = rest?.defaultValue as ApiOption | undefined;
    const selectedVal = selectedOption as ApiOption | undefined;

    const filteredOptions = dataOptions.filter((opt) => {
      const isDefault =
        defaultVal?.value && String(opt.value) === String(defaultVal.value);
      const isSelected =
        selectedVal?.value && String(opt.value) === String(selectedVal.value);

      return !isDefault && !isSelected;
    });

    const formattedOptions = [
      ...(selectedVal?.value ? [selectedVal] : []),
      ...(defaultVal?.value &&
      String(defaultVal.value) !== String(selectedVal?.value)
        ? [defaultVal]
        : []),
      ...filteredOptions,
    ];

    setOptions((prev) => {
      if (prev.length === 0) return formattedOptions;

      const merged = [...prev];

      for (const newOpt of formattedOptions) {
        if (!merged.some((o) => String(o.value) === String(newOpt.value))) {
          merged.push(newOpt);
        }
      }
      return merged;
    });
  }, [
    items,
    queryEnabled,
    isSuccess,
    keyKey,
    valueKey,
    labelKey,
    rest?.defaultValue,
    selectedValue,
  ]);

  return (
    <GeneralSelect
      {...rest}
      loadingMessage={()=>t("loading")}
      filterOption={() => true}
      name={name}
      onInputChange={(inputValue) => {
        setSearchQuery(inputValue);
        onInputChange?.(inputValue);
      }}
      options={options}
      isPending={isFetchingNextPage || isLoading || isPending}
      onMenuScrollToBottom={() => hasNextPage && fetchNextPage()}
      placeholder={placeholder}
      components={{ MenuList: CustomMenuList }}
      onChange={onChange}
      value={selectedOption}
      isDisabled={readOnly || rest.isDisabled}
    />
  );
};

export default GeneralSelectApi;

const CustomMenuList = (props: any) => {
  const listRef = useRef<HTMLDivElement>(null);

  const handleScroll = (e: Event) => {
    const target = e.target as HTMLDivElement;
    const { scrollTop, scrollHeight, clientHeight } = target;
    const isBottom = scrollHeight - scrollTop <= clientHeight + 5;

    if (isBottom && props.selectProps.onMenuScrollToBottom) {
      props.selectProps.onMenuScrollToBottom();
    }
  };

  useEffect(() => {
    const node = listRef.current;
    if (!node) return;

    node.addEventListener("scroll", handleScroll);
    return () => node.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <components.MenuList {...props} innerRef={listRef}>
      {props.children}
    </components.MenuList>
  );
};
